import { ColumnDef } from '@tanstack/react-table'
import { cn } from '@/lib/utils'
import LongText from '@/components/long-text'
import { Client, Person } from '../data/schema'
import { DataTableColumnHeader } from '../../../components/data-table-column-header'
import { DataTableRowActions } from './data-table-row-actions'
import i18n from '@/i18n'
import { Link, useRouter } from '@tanstack/react-router'

export const columns: ColumnDef<Client>[] = [
	{
		accessorKey: 'name',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('name')} />
		),
		cell: ({ row }) => {
			const { name, id } = row.original
			const router = useRouter()

			const handleClientClick = (e: React.MouseEvent) => {
				e.preventDefault()
				router.navigate({
					to: '/cases',
					search: { client: id }
				})
			}

			return (
				<button
					onClick={handleClientClick}
					className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
				>
					<LongText className='max-w-36'>{name}</LongText>
				</button>
			)
		},
		meta: {
			className: cn(
				'drop-shadow-[0_1px_2px_rgb(0_0_0_/_0.1)] dark:drop-shadow-[0_1px_2px_rgb(255_255_255_/_0.1)] lg:drop-shadow-none',
				'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
				'sticky right md:table-cell'
			),
		},
		enableHiding: false,
	},
	{
		accessorKey: 'client_type',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('client_type')} />
		),
		cell: ({ row }) => {
			const type = row.getValue('client_type') as string
			return (
				<div className='w-fit text-nowrap'>
					{type === 'individual' ? i18n.t('individual') : i18n.t('corporate')}
				</div>
			)
		},
	},
	{
		id: 'idnumber',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('idnumber')} />
		),
		cell: ({ row }) => (
			<div className='w-fit text-nowrap'>{row.original.idnumber || ''}</div>
		),
	},
	{
		id: 'email',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('email')} />
		),
		cell: ({ row }) => (
			<div className='w-fit text-nowrap'>{row.original.email}</div>
		),
	},
	{
		id: 'phone',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('phone')} />
		),
		cell: ({ row }) => (
			<div dir='ltr' className='w-fit text-nowrap'>{row.original.phone}</div>
		),
	},
	{
		accessorKey: 'added_by',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('added_by')} />
		),
		cell: ({ row }) => {
			const addedBy = row.getValue('added_by') as Person
			return (
				<Link
					to="/users/$id"
					params={{ id: addedBy.id }}
					className='w-fit text-nowrap hover:underline'
					onClick={(e: React.MouseEvent) => e.stopPropagation()} // Stop event propagation
				>
					{addedBy.name}
				</Link>
			)
		},
	},
	{
		accessorKey: 'created_at',
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={i18n.t('created_at')} />
		),
		cell: ({ row }) => (
			<div className='w-fit text-nowrap'>
				{(row.getValue('created_at') as Date).toLocaleDateString('en-GB')}
			</div>
		),
		enableSorting: true
	},
	{
		id: 'actions',
		cell: DataTableRowActions,
	},
]
